import { NullableRecord, mapValues } from '@dozuki/web-js/utils';
import {
   StageStrictness,
   TrainingDoctype,
} from '~/pages/api/courses/_shared/course-form/course-stage-editor/types';

import {
   IconButton,
   Popover,
   PopoverTrigger,
   PopoverContent,
   PopoverArrow,
   PopoverCloseButton,
   PopoverHeader,
   PopoverBody,
   Select,
   Text,
} from '@chakra-ui/react';
import { ChangeEvent } from 'react';
import { useLocalization } from '~/framework';
import { Icon, B } from '~/ui-components';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { Stage } from '../Stage';

type StageStrictnessEditorProps = {
   stage: Stage;
   onChange: (newStageValues: Stage) => void;
};

export function StageStrictnessEditor({ stage, onChange }: StageStrictnessEditorProps) {
   const { _js } = useLocalization();

   const stageStrictnessDescriptions: NullableRecord<
      TrainingDoctype,
      Partial<NullableRecord<StageStrictness, string>>
   > = {
      guide: {
         null: _js('None'),
         minor: _js('All Releases'),
         major: _js('Major'),
      },
      external_training_module: {
         null: _js('None'),
         minor: _js('All Updates'),
      },
      wiki: {
         null: _js('None'),
         minor: _js('All Releases'),
         major: _js('Major'),
      },
   };

   function handleChange(e: ChangeEvent<HTMLSelectElement>) {
      onChange({
         ...stage,
         strictness: e.target.value === 'null' ? null : (e.target.value as StageStrictness),
      });
   }

   const strictnessDescriptionsForDoctype = stageStrictnessDescriptions[stage.doctype];

   return (
      <Popover>
         <PopoverTrigger>
            <IconButton
               aria-label={_js('Change Stage Invalidation')}
               icon={<Icon type={faInfoCircle} />}
            />
         </PopoverTrigger>
         <PopoverContent>
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverHeader>{_js('Stage Invalidation')}</PopoverHeader>
            <PopoverBody>
               <Select value={stage.strictness?.toString()} onChange={handleChange}>
                  {mapValues(strictnessDescriptionsForDoctype, (description, stageStrictness) => (
                     <option value={stageStrictness} key={stageStrictness}>
                        {description}
                     </option>
                  ))}
               </Select>
               <Text>
                  {_js(
                     'When there are changes to a document, invalidation requires the user to recomplete the course.'
                  )}
               </Text>
               {stage.doctype === 'guide' && (
                  <>
                     <Text>
                        <B>{_js('Major releases')}</B>-{' '}
                        {_js('any major releases will require recompletion')}
                     </Text>
                     <Text>
                        <B>{_js('All releases')}</B>-{' '}
                        {_js('all releases will require recompletion')}
                     </Text>
                  </>
               )}
            </PopoverBody>
         </PopoverContent>
      </Popover>
   );
}
